/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — undo/redo command stack.

   EXPANDED (People Rebuild §3, granted): the old stack only covered
   move / edge-create / edge-delete. This one ALSO covers waypoint
   bends, routing/style/color/label changes on an edge, and junction
   spawn/delete — every user-visible structural or cosmetic edit now
   has an inverse on the stack. Same shape as every serious editor:
   max 50 entries, redo cleared on any new user action.

   EDIT-SESSION MODEL (2026-08-17): the `deps` callbacks below are no
   longer network writes — the caller (OrgChartV2) passes pure local
   session-state mutators (mutate React Flow nodes/edges + note the
   change in the session's diff bookkeeping). Undo/redo therefore stay
   entirely local for the life of a session; the stack is cleared when
   a session begins/ends (nothing to "survive reload" mid-session —
   the whole point of the model is one atomic commit on Save).
   ═══════════════════════════════════════════════════════════════ */

import { useCallback, useRef } from 'react';
import type { OrgChartEdge, OrgChartJunction, OrgChartNote } from '../../../../../shared/entities';
import type { PositionUpdate } from '../types';

export type MoveUpdate = { id: string; prevX: number; prevY: number; newX: number; newY: number };

/** The subset of `OrgChartEdge` fields an inline edit (waypoints,
 *  routing side, style, color, label, reset-shape) can touch — used
 *  for the edge-patch undo command's before/after snapshot. */
export type EdgePatchFields = Partial<
  Pick<OrgChartEdge, 'waypoints' | 'style' | 'color' | 'label' | 'sourceHandle' | 'targetHandle'>
>;

export type UndoCmd =
  | { kind: 'move'; updates: MoveUpdate[] }
  | { kind: 'edge-create'; edge: OrgChartEdge }
  | { kind: 'edge-delete'; edge: OrgChartEdge }
  | { kind: 'edge-patch'; edgeId: string; prev: EdgePatchFields; next: EdgePatchFields }
  | {
      kind: 'junction-spawn';
      junction: OrgChartJunction;
      newEdge: OrgChartEdge;
      retargetedEdgeId: string;
      prevRetarget: EdgePatchFields & {
        targetMemberId?: string;
        targetType?: OrgChartEdge['targetType'];
      };
      nextRetarget: EdgePatchFields & {
        targetMemberId?: string;
        targetType?: OrgChartEdge['targetType'];
      };
    }
  | { kind: 'junction-create'; junction: OrgChartJunction }
  | { kind: 'junction-delete'; junction: OrgChartJunction; edges: OrgChartEdge[] }
  | { kind: 'note-create'; note: OrgChartNote }
  | { kind: 'note-delete'; note: OrgChartNote }
  | { kind: 'note-patch'; noteId: string; prev: NotePatchFields; next: NotePatchFields };

/** The subset of `OrgChartNote` fields an inline edit (move, text edit,
 *  font size, color) can touch — used for the note-patch undo command's
 *  before/after snapshot, same shape convention as `EdgePatchFields`. */
export type NotePatchFields = Partial<
  Pick<OrgChartNote, 'x' | 'y' | 'text' | 'fontSize' | 'color' | 'width'>
>;

export interface UseUndoDeps {
  onSavePositions: (updates: PositionUpdate[]) => void;
  onCreateEdge: (edge: Partial<OrgChartEdge>) => Promise<void>;
  onUpdateEdge: (id: string, patch: Partial<OrgChartEdge>) => Promise<void>;
  onDeleteEdge: (id: string) => Promise<void>;
  onCreateJunction: (x: number, y: number) => Promise<OrgChartJunction | undefined>;
  onDeleteJunction: (id: string) => Promise<void>;
  onCreateNote: (note: OrgChartNote) => Promise<void>;
  onUpdateNote: (id: string, patch: NotePatchFields) => Promise<void>;
  onDeleteNote: (id: string) => Promise<void>;
}

const MAX_STACK = 50;

export function useUndo(deps: UseUndoDeps) {
  const undoStack = useRef<UndoCmd[]>([]);
  const redoStack = useRef<UndoCmd[]>([]);

  const pushUndo = useCallback((cmd: UndoCmd) => {
    undoStack.current.push(cmd);
    if (undoStack.current.length > MAX_STACK) undoStack.current.shift();
    redoStack.current = [];
  }, []);

  const applyCmd = useCallback(
    async (cmd: UndoCmd, isRedo: boolean): Promise<UndoCmd> => {
      switch (cmd.kind) {
        case 'move': {
          const updates = cmd.updates.map((u) =>
            isRedo
              ? { id: u.id, orgX: u.newX, orgY: u.newY }
              : { id: u.id, orgX: u.prevX, orgY: u.prevY }
          );
          await deps.onSavePositions(updates);
          return {
            kind: 'move',
            updates: cmd.updates.map((u) => ({
              id: u.id,
              prevX: isRedo ? u.prevX : u.newX,
              prevY: isRedo ? u.prevY : u.newY,
              newX: isRedo ? u.newX : u.prevX,
              newY: isRedo ? u.newY : u.prevY,
            })),
          };
        }
        case 'edge-create': {
          if (isRedo) {
            await deps.onCreateEdge(cmd.edge);
            return { kind: 'edge-create', edge: cmd.edge };
          }
          await deps.onDeleteEdge(cmd.edge.id);
          return { kind: 'edge-delete', edge: cmd.edge };
        }
        case 'edge-delete': {
          if (isRedo) {
            await deps.onDeleteEdge(cmd.edge.id);
            return { kind: 'edge-delete', edge: cmd.edge };
          }
          await deps.onCreateEdge(cmd.edge);
          return { kind: 'edge-create', edge: cmd.edge };
        }
        case 'edge-patch': {
          const apply = isRedo ? cmd.next : cmd.prev;
          await deps.onUpdateEdge(cmd.edgeId, apply);
          return { kind: 'edge-patch', edgeId: cmd.edgeId, prev: cmd.next, next: cmd.prev };
        }
        case 'junction-spawn': {
          if (isRedo) {
            // Re-create the junction+edge, re-point the original.
            const j = await deps.onCreateJunction(cmd.junction.x, cmd.junction.y);
            if (j) await deps.onCreateEdge({ ...cmd.newEdge, sourceMemberId: j.id });
            await deps.onUpdateEdge(cmd.retargetedEdgeId, cmd.nextRetarget);
            return cmd;
          }
          // Undo: delete the junction (cascades its edges server/client
          // side) and restore the original edge's prior target.
          await deps.onDeleteJunction(cmd.junction.id);
          await deps.onUpdateEdge(cmd.retargetedEdgeId, cmd.prevRetarget);
          return cmd;
        }
        case 'junction-create': {
          if (isRedo) {
            // Same id-freshness caveat as junction-delete's undo: recreation
            // mints a new id at the same spot.
            await deps.onCreateJunction(cmd.junction.x, cmd.junction.y);
            return cmd;
          }
          await deps.onDeleteJunction(cmd.junction.id);
          return cmd;
        }
        case 'junction-delete': {
          if (isRedo) {
            await deps.onDeleteJunction(cmd.junction.id);
            return cmd;
          }
          // Undo: junctions don't have an onCreateJunction(id,...) that
          // preserves the original id, so restoring means creating a
          // fresh junction at the same spot and re-creating its edges
          // pointed at the new id.
          const j = await deps.onCreateJunction(cmd.junction.x, cmd.junction.y);
          if (j) {
            for (const e of cmd.edges) {
              const remapped: Partial<OrgChartEdge> = { ...e };
              if (e.sourceType === 'junction' && e.sourceMemberId === cmd.junction.id) {
                remapped.sourceMemberId = j.id;
              }
              if (e.targetType === 'junction' && e.targetMemberId === cmd.junction.id) {
                remapped.targetMemberId = j.id;
              }
              await deps.onCreateEdge(remapped);
            }
          }
          return cmd;
        }
        case 'note-create': {
          if (isRedo) {
            await deps.onCreateNote(cmd.note);
            return { kind: 'note-create', note: cmd.note };
          }
          await deps.onDeleteNote(cmd.note.id);
          return { kind: 'note-delete', note: cmd.note };
        }
        case 'note-delete': {
          if (isRedo) {
            await deps.onDeleteNote(cmd.note.id);
            return { kind: 'note-delete', note: cmd.note };
          }
          await deps.onCreateNote(cmd.note);
          return { kind: 'note-create', note: cmd.note };
        }
        case 'note-patch': {
          const apply = isRedo ? cmd.next : cmd.prev;
          await deps.onUpdateNote(cmd.noteId, apply);
          return { kind: 'note-patch', noteId: cmd.noteId, prev: cmd.next, next: cmd.prev };
        }
      }
    },
    [deps]
  );

  const doUndo = useCallback(async () => {
    const cmd = undoStack.current.pop();
    if (!cmd) return;
    const inverse = await applyCmd(cmd, false);
    redoStack.current.push(inverse);
  }, [applyCmd]);

  const doRedo = useCallback(async () => {
    const cmd = redoStack.current.pop();
    if (!cmd) return;
    const inverse = await applyCmd(cmd, true);
    undoStack.current.push(inverse);
  }, [applyCmd]);

  return { pushUndo, doUndo, doRedo, undoStack, redoStack };
}
